import React, { useState, useCallback, useEffect } from 'react';
import { cn } from 'lib/utils';
import ReactDOM from 'react-dom';
import { X, ArrowLeft, ArrowRight, Sparkles, Star } from 'lucide-react';
import { OnboardingQuestion, QuestionOption } from './OnboardingQuestion';
import { OnboardingResults } from './OnboardingResults';
import { OnboardingAnswer, calculateRecommendations, FeatureRecommendation } from 'utils/onboardingRecommendations';
import {
  FileText,
  HeartPulse,
  Target,
  NotebookPen,
  MapPin,
  Brain,
  ClipboardCheck,
  Headphones,
  Moon,
  Activity,
  Apple,
  Heart,
  TrendingUp
} from 'lucide-react';

const QUESTION_1_OPTIONS: QuestionOption[] = [
  {
    id: 'understand-records',
    label: 'Understand my health records',
    description: 'Explore and make sense of your medical history from Swedish healthcare',
    icon: <FileText size={20} />
  },
  {
    id: 'manage-condition',
    label: 'Manage a health condition',
    description: 'Get support for chronic conditions with CBT programs and tracking',
    icon: <HeartPulse size={20} />
  },
  {
    id: 'improve-wellbeing',
    label: 'Improve my overall wellbeing',
    description: 'Focus on sleep, movement, nutrition, and personal growth',
    icon: <TrendingUp size={20} />
  },
  {
    id: 'prepare-visits',
    label: 'Prepare for healthcare visits',
    description: 'Get ready for appointments and create care request documents',
    icon: <Target size={20} />
  },
  {
    id: 'track-health',
    label: 'Track my health over time',
    description: 'Build habits, monitor symptoms, and see your progress',
    icon: <NotebookPen size={20} />
  }
];

const QUESTION_2_OPTIONS: QuestionOption[] = [
  {
    id: 'has-records',
    label: 'I have health records to explore',
    description: 'You have medical records from 1177 or other Swedish healthcare systems',
    icon: <FileText size={20} />
  },
  {
    id: 'chronic-condition',
    label: "I'm managing a chronic condition",
    description: 'You have an ongoing health condition that needs regular attention',
    icon: <HeartPulse size={20} />
  },
  {
    id: 'learn-topics',
    label: 'I want to understand myself better',
    description: 'You want to explore emotions, thinking patterns, and relationships',
    icon: <Heart size={20} />
  },
  {
    id: 'new-tracking',
    label: "I'm starting to track my health",
    description: 'You want to begin monitoring your health and building healthy habits',
    icon: <NotebookPen size={20} />
  },
  {
    id: 'navigate-care',
    label: 'I need help finding care',
    description: 'You need support navigating the Swedish healthcare system',
    icon: <MapPin size={20} />
  }
];

const QUESTION_3_OPTIONS: QuestionOption[] = [
  {
    id: 'get-data',
    label: 'Import my healthcare data',
    description: 'Get records from 1177 or upload existing files',
    icon: <FileText size={20} />
  },
  {
    id: 'take-assessment',
    label: 'Take a health assessment',
    description: 'Understand your physical and mental wellbeing',
    icon: <ClipboardCheck size={20} />
  },
  {
    id: 'find-program',
    label: 'Find a CBT program',
    description: 'Evidence-based programs for ADHD, anxiety, depression',
    icon: <Brain size={20} />
  },
  {
    id: 'relax-now',
    label: 'Relax and de-stress',
    description: 'Breathing exercises, soundscapes, and meditations',
    icon: <Headphones size={20} />
  },
  {
    id: 'improve-sleep',
    label: 'Improve my sleep',
    description: 'Evidence-based strategies for better rest',
    icon: <Moon size={20} />
  },
  {
    id: 'improve-fitness',
    label: 'Get more active',
    description: 'Build a sustainable movement practice',
    icon: <Activity size={20} />
  },
  {
    id: 'eat-better',
    label: 'Improve nutrition',
    description: 'Learn about food and hydration',
    icon: <Apple size={20} />
  },
  {
    id: 'understand-self',
    label: 'Understand myself better',
    description: 'Emotions, relationships, and thinking patterns',
    icon: <Heart size={20} />
  }
];

interface PersonalizedOnboardingProps {
  isOpen: boolean;
  onComplete: (recommendations: FeatureRecommendation[], answers: OnboardingAnswer) => void;
  onSkip: () => void;
}

export const PersonalizedOnboarding: React.FC<PersonalizedOnboardingProps> = ({
  isOpen,
  onComplete,
  onSkip
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<OnboardingAnswer>({
    primaryGoal: [],
    immediateNeeds: []
  });
  const [recommendations, setRecommendations] = useState<FeatureRecommendation[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setAnswers({ primaryGoal: [], immediateNeeds: [] });
      setRecommendations([]);
    }
  }, [isOpen]);

  const handleSelect = useCallback((id: string) => {
    if (currentStep === 0) {
      setAnswers(prev => {
        const currentGoals = Array.isArray(prev.primaryGoal) ? prev.primaryGoal : (prev.primaryGoal ? [prev.primaryGoal] : []);
        const newGoals = currentGoals.includes(id)
          ? currentGoals.filter(g => g !== id)
          : [...currentGoals, id];
        return { ...prev, primaryGoal: newGoals };
      });
    } else if (currentStep === 1) {
      setAnswers(prev => {
        const currentSituations = Array.isArray(prev.currentSituation) ? prev.currentSituation : (prev.currentSituation ? [prev.currentSituation] : []);
        const newSituations = currentSituations.includes(id)
          ? currentSituations.filter(s => s !== id)
          : [...currentSituations, id];
        return { ...prev, currentSituation: newSituations };
      });
    } else if (currentStep === 2) {
      setAnswers(prev => {
        const currentNeeds = prev.immediateNeeds || [];
        const newNeeds = currentNeeds.includes(id)
          ? currentNeeds.filter(n => n !== id)
          : [...currentNeeds, id];
        return { ...prev, immediateNeeds: newNeeds };
      });
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 2) {
      const calculated = calculateRecommendations(answers);
      setRecommendations(calculated);
      setCurrentStep(3);
    } else if (currentStep < 2) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, answers]);

  const handleBack = useCallback(() => {
    if (currentStep > 0 && currentStep < 3) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleGetStarted = useCallback(() => {
    onComplete(recommendations, answers);
  }, [recommendations, answers, onComplete]);

  const handleSkip = useCallback(() => {
    const defaultAnswers: OnboardingAnswer = {
      primaryGoal: ['improve-wellbeing'],
      currentSituation: ['new-tracking'],
      immediateNeeds: ['take-assessment']
    };
    const defaultRecommendations = calculateRecommendations(defaultAnswers);
    onComplete(defaultRecommendations, defaultAnswers);
  }, [onComplete]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const canProceed =
    (currentStep === 0 && Array.isArray(answers.primaryGoal) && answers.primaryGoal.length > 0) ||
    (currentStep === 0 && !Array.isArray(answers.primaryGoal) && answers.primaryGoal) ||
    (currentStep === 1 && Array.isArray(answers.currentSituation) && answers.currentSituation.length > 0) ||
    (currentStep === 1 && !Array.isArray(answers.currentSituation) && answers.currentSituation) ||
    (currentStep === 2 && answers.immediateNeeds && answers.immediateNeeds.length > 0);

  const portalContent = (
    <div
      className="fixed inset-0 z-modal bg-slate-900/95 backdrop-blur-2xl flex items-center justify-center p-4 overflow-y-auto max-md:p-2 max-md:items-start max-md:pt-8"
      onClick={(e) => e.target === e.currentTarget && handleSkip()}
    >
      <div
        className="bg-card rounded-lg w-full max-w-[680px] max-h-[calc(100vh-2rem)] flex flex-col relative shadow-lg border overflow-hidden max-md:max-h-[calc(100vh-1rem)] max-md:max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 bg-card z-10 border-b max-md:px-5">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-white shrink-0">
              <Star size={20} />
            </div>
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <h2 className="text-xl font-semibold text-foreground-strong m-0 leading-tight">
                Welcome to EIR Space
              </h2>
              <span className="text-sm text-foreground-muted flex items-center gap-2 flex-wrap max-[480px]:text-xs">
                {currentStep < 3 ? (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/[0.12] rounded-md font-semibold text-primary text-xs whitespace-nowrap">
                      <Sparkles size={10} />
                      {currentStep + 1} of 3
                    </span>
                    Personalize your experience
                  </>
                ) : (
                  'Your personalized recommendations'
                )}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="bg-muted0/8 border-none text-foreground-muted cursor-pointer p-2.5 rounded-lg transition-all flex items-center justify-center shrink-0 hover:bg-accent0/12 hover:text-foreground-strong active:scale-95 max-[480px]:p-2"
            onClick={handleSkip}
            aria-label="Skip onboarding"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 flex-1 flex flex-col gap-6 min-h-0 overflow-y-auto max-md:px-6 max-md:pb-5 max-md:gap-5 max-[480px]:px-5 max-[480px]:pb-4 max-[480px]:gap-4">
          {/* Progress steps */}
          {currentStep < 3 && (
            <div className="flex items-center gap-2 mb-1 pt-2">
              {[0, 1, 2].map((step) => (
                <div
                  key={step}
                  className={cn(
                    'flex-1 h-[5px] rounded-full transition-all duration-400',
                    step < currentStep
                      ? 'bg-primary'
                      : step === currentStep
                        ? 'bg-primary/[0.35]'
                        : 'bg-muted0/12'
                  )}
                />
              ))}
            </div>
          )}

          {currentStep === 0 && (
            <OnboardingQuestion
              title="What brings you to EIR Space?"
              description="Select all that apply — we'll personalize your experience based on what matters most to you"
              options={QUESTION_1_OPTIONS}
              selectedIds={Array.isArray(answers.primaryGoal) ? answers.primaryGoal : (answers.primaryGoal ? [answers.primaryGoal] : [])}
              onSelect={handleSelect}
              multiple
            />
          )}
          {currentStep === 1 && (
            <OnboardingQuestion
              title="What describes your current situation?"
              description="Select all that apply — this helps us recommend the right starting point"
              options={QUESTION_2_OPTIONS}
              selectedIds={Array.isArray(answers.currentSituation) ? answers.currentSituation : (answers.currentSituation ? [answers.currentSituation] : [])}
              onSelect={handleSelect}
              multiple
            />
          )}
          {currentStep === 2 && (
            <OnboardingQuestion
              title="What would help you most right now?"
              description="Select all that apply — you can always explore more later"
              options={QUESTION_3_OPTIONS}
              selectedIds={answers.immediateNeeds || []}
              onSelect={handleSelect}
              multiple
            />
          )}
          {currentStep === 3 && (
            <OnboardingResults
              recommendations={recommendations}
              onGetStarted={handleGetStarted}
            />
          )}
        </div>

        {/* Footer */}
        {currentStep < 3 && (
          <div className="flex items-center justify-between px-8 py-5 border-t border-slate-500/8 gap-4 bg-muted/60 backdrop-blur-sm rounded-b-3xl shrink-0 max-md:px-6 max-md:py-4 max-md:flex-wrap max-[480px]:px-5 max-[480px]:py-4 max-[480px]:gap-3">
            <div>
              {currentStep > 0 && (
                <button
                  type="button"
                  className="flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm cursor-pointer transition-all border-none bg-muted0/8 text-foreground-muted hover:enabled:bg-muted0/12 disabled:opacity-50 disabled:cursor-not-allowed max-[480px]:px-5 max-[480px]:py-3 max-[480px]:text-sm"
                  onClick={handleBack}
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3 items-center flex-wrap max-md:w-full max-md:justify-between">
              <button
                type="button"
                className="bg-transparent border-none text-foreground-muted text-sm font-medium cursor-pointer px-4 py-3 rounded-lg transition-all whitespace-nowrap hover:bg-accent0/8 hover:text-foreground-muted max-[480px]:text-sm max-[480px]:px-3.5 max-[480px]:py-2.5"
                onClick={handleSkip}
              >
                Skip for now
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm cursor-pointer transition-all border-none whitespace-nowrap bg-primary text-white shadow-lg hover:enabled:-translate-y-px hover:enabled:shadow-lg active:enabled:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none max-[480px]:px-5 max-[480px]:py-3 max-[480px]:text-sm"
                onClick={handleNext}
                disabled={!canProceed}
              >
                {currentStep === 2 ? 'See Recommendations' : 'Continue'}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(portalContent, document.body);
};
